import { useRef, useEffect, useMemo, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import { dampenAnimation, createAdditiveAnimation } from "./animationControls";

const TALKING_ANIMATION_INTENSITY = 0.25; // Reduced from 0.3 for more subtle movement
const ANIMATION_FADE_SPEED = 6; // Reduced from 8 for smoother transitions

function Avatar({
  url,
  idleAnimationUrl,
  talkingAnimationUrl,
  animationQueueRef,
  isSpeaking,
}) {
  const groupRef = useRef();
  const { scene: avatarScene } = useGLTF(url);
  const { animations: idleAnimation } = useGLTF(idleAnimationUrl);
  const { animations: talkingAnimation } = useGLTF(talkingAnimationUrl);

  const clonedAvatar = useMemo(() => {
    if (!avatarScene) return null;
    return SkeletonUtils.clone(avatarScene);
  }, [avatarScene]);

  const additiveTalkingClip = useMemo(() => {
    if (!clonedAvatar || !talkingAnimation.length) return null;
    const dampenedClip = dampenAnimation(
      talkingAnimation[0],
      clonedAvatar,
      TALKING_ANIMATION_INTENSITY
    );
    return createAdditiveAnimation(dampenedClip);
  }, [clonedAvatar, talkingAnimation]);

  const allAnimations = useMemo(() => {
    return additiveTalkingClip
      ? [...idleAnimation, additiveTalkingClip]
      : idleAnimation;
  }, [idleAnimation, additiveTalkingClip]);

  const { actions } = useAnimations(allAnimations, groupRef);
  const idleActionName = idleAnimation[0]?.name;
  const talkingActionName = additiveTalkingClip?.name;

  useEffect(() => {
    const idleAction = actions[idleActionName];
    const talkingAction = actions[talkingActionName];
    if (idleAction) {
      idleAction.play();
    }
    if (talkingAction) {
      talkingAction.play();
      talkingAction.weight = 0;
    }
  }, [actions, idleActionName, talkingActionName]);

  const [headMesh, setHeadMesh] = useState(null);
  const [visemeDict, setVisemeDict] = useState(null);
  const smoothness = 0.3; // Increased from 0.25 for more responsive lip sync
  const currentViseme = useRef({ viseme: "viseme_sil", duration: 0 });
  const timeToNextViseme = useRef(0);

  useFrame((_, delta) => {
    const talkingAction = actions[talkingActionName];
    if (talkingAction) {
      const targetWeight = isSpeaking ? 1 : 0;
      talkingAction.weight = THREE.MathUtils.lerp(
        talkingAction.weight,
        targetWeight,
        delta * ANIMATION_FADE_SPEED
      );
    }

    if (!headMesh || !visemeDict || !headMesh.morphTargetInfluences) return;

    timeToNextViseme.current -= delta;

    if (timeToNextViseme.current <= 0 && animationQueueRef.current.length > 0) {
      const next = animationQueueRef.current.shift();
      currentViseme.current = next;
      timeToNextViseme.current = next.duration;
    }
    if (animationQueueRef.current.length === 0 && !isSpeaking) {
      currentViseme.current = { viseme: "viseme_sil", duration: 0 };
    }

    const influences = headMesh.morphTargetInfluences;
    const targetVisemeIndex = visemeDict[currentViseme.current.viseme];
    for (let i = 0; i < influences.length; i++) {
      if (i === targetVisemeIndex) {
        influences[i] = THREE.MathUtils.lerp(influences[i], 1, smoothness);
      } else {
        influences[i] = THREE.MathUtils.lerp(influences[i], 0, smoothness);
      }
    }
  });

  useEffect(() => {
    if (!clonedAvatar) return;
    clonedAvatar.traverse((child) => {
      if (
        child.isMesh &&
        child.name.includes("Wolf3D_Head") &&
        child.morphTargetDictionary
      ) {
        setHeadMesh(child);
        setVisemeDict(child.morphTargetDictionary);
      }
    });
  }, [clonedAvatar]);

  if (!clonedAvatar) return null;
  return (
    <primitive
      ref={groupRef}
      object={clonedAvatar}
      scale={6.5}
      position={[0, -9.5, -1]}
    />
  );
}

export default Avatar;
